import React from "react";
import {
  Document,
  Font,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";
import PoppinsRegular from "../fonts/Poppins-Regular.ttf";

Font.register({ family: "Poppins", src: PoppinsRegular });

// ─── Helpers ──────────────────────────────────────────────────────────────────

const stripHtml = (html) => {
  if (!html) return "";
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<[^>]+>/g, "");
  const txt = document.createElement("textarea");
  txt.innerHTML = withBreaks;
  return txt.value.replace(/\n{3,}/g, "\n\n").trim();
};

const getRoundDate = (r) => {
  const raw = r.assigneedate || r.createdat;
  if (!raw) return null;
  if (typeof raw === "object" && raw.$date) return String(raw.$date).split("T")[0];
  const str = String(raw);
  if (str.startsWith("-621")) return null;
  return str.split("T")[0];
};

const getSalaryRangeLabel = (expectedSalaryRaw, minSalary, maxSalary) => {
  const val = expectedSalaryRaw;
  if (!val || (!minSalary && !maxSalary)) return null;
  if (val < minSalary) return { label: "Below Range", color: "#8F94A1" };
  if (val > maxSalary) return { label: "Above Range", color: "#E95060" };
  return { label: "Within Range", color: "#40B2B7" };
};

// ─── Design tokens ────────────────────────────────────────────────────────────

const C = {
  blue: "#148EFF",
  darkText: "#161A21",
  mutedText: "#6B7280",
  border: "#E5E7EB",
  bg: "#F0F4FC",
  redAlert: "#E95060",
  amber: "#F59E0B",
  amberBg: "#FFFBEB",
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  page: {
    fontFamily: "Poppins",
    fontSize: 9,
    color: C.darkText,
    backgroundColor: "#F8FAFC",
    paddingTop: 24,
    paddingBottom: 28,
    paddingHorizontal: 28,
  },

  // ── Section card ──
  section: {
    marginBottom: 8,
    backgroundColor: "#fff",
    borderRadius: 8,
    border: `1 solid ${C.border}`,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderBottom: `1 solid ${C.border}`,
    backgroundColor: "#fff",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  sectionBar: {
    width: 3,
    height: 13,
    backgroundColor: C.blue,
    borderRadius: 2,
    marginRight: 7,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: C.darkText,
  },
  sectionBody: {
    padding: 10,
  },

  // ── Candidate header ──
  candidateHeader: {
    marginBottom: 8,
    backgroundColor: "#fff",
    borderRadius: 8,
    border: `1 solid ${C.border}`,
    padding: 10,
  },
  nameRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 5,
    flexWrap: "wrap",
    gap: 5,
  },
  candidateName: {
    fontSize: 13,
    fontWeight: 700,
  },
  jobTitle: {
    fontSize: 9,
    fontWeight: 600,
    color: C.mutedText,
  },
  statusBadge: {
    fontSize: 8,
    fontWeight: 600,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    border: `1 solid #9CA3AF`,
    color: "#374151",
  },
  infoRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 2,
    alignItems: "center",
  },
  infoLabel: {
    fontSize: 8,
    fontWeight: 700,
    color: C.darkText,
    marginRight: 3,
  },
  infoValue: {
    fontSize: 8,
    color: C.mutedText,
    marginRight: 12,
  },

  // ── Skills ──
  skillsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 5,
  },
  skillTag: {
    fontSize: 8,
    fontWeight: 600,
    paddingHorizontal: 7,
    paddingVertical: 3,
    border: `1 solid ${C.border}`,
    borderRadius: 5,
    backgroundColor: C.bg,
    color: C.darkText,
  },

  // ── Two-column (salary + time to join) ──
  twoCol: {
    flexDirection: "row",
  },
  col: {
    flex: 1,
  },
  colDivider: {
    width: 1,
    backgroundColor: C.border,
  },
  salaryBadge: {
    fontSize: 7,
    fontWeight: 600,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 4,
    border: `1 solid`,
    marginLeft: 7,
  },
  salaryRow: {
    flexDirection: "row",
    gap: 20,
    marginTop: 5,
  },
  salaryLabel: {
    fontSize: 8,
    color: C.mutedText,
    marginBottom: 2,
  },
  salaryValue: {
    fontSize: 11,
    fontWeight: 700,
  },

  // ── AI Score grid ──
  scoreGrid: {
    flexDirection: "row",
    marginBottom: 6,
  },
  scoreItem: {
    flex: 1,
    paddingRight: 10,
  },
  scoreLabel: {
    fontSize: 7,
    fontWeight: 600,
    color: C.mutedText,
    marginBottom: 3,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  scoreValue: {
    fontSize: 12,
    fontWeight: 700,
    color: C.blue,
    marginBottom: 3,
  },
  progressTrack: {
    height: 5,
    backgroundColor: "#E7F4FF",
    borderRadius: 3,
  },
  progressFill: {
    height: 5,
    backgroundColor: C.blue,
    borderRadius: 3,
  },

  // ── Sub-section titles (Technical / Employability score lines) ──
  subSectionTitle: {
    fontSize: 9,
    fontWeight: 700,
    marginBottom: 5,
    color: C.darkText,
  },
  subSectionDivider: {
    borderTop: `1 solid ${C.border}`,
    marginTop: 10,
  },
  subSectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 0,
    paddingVertical: 7,
    marginBottom: 8,
  },
  subSectionBar: {
    width: 3,
    height: 13,
    backgroundColor: C.blue,
    borderRadius: 2,
    marginRight: 7,
  },
  subSectionName: {
    fontSize: 10,
    fontWeight: 700,
    color: C.darkText,
  },
  summaryText: {
    fontSize: 9,
    color: C.mutedText,
    lineHeight: 1.7,
  },

  // ── Cheating alert ──
  cheatAlert: {
    backgroundColor: "#FEF2F2",
    border: `1 solid #FECACA`,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 7,
    marginBottom: 10,
  },
  cheatText: {
    fontSize: 8,
    color: C.redAlert,
    fontWeight: 600,
    lineHeight: 1.4,
  },

  // ── Soft skill bars ──
  softSkillRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 5,
  },
  softSkillLabel: {
    width: 100,
    fontSize: 8,
    color: C.darkText,
    fontWeight: 600,
  },
  softSkillTrack: {
    flex: 1,
    height: 5,
    backgroundColor: "#E7F4FF",
    borderRadius: 3,
    marginHorizontal: 8,
  },
  softSkillFill: {
    height: 5,
    backgroundColor: "rgba(99,102,241,0.85)",
    borderRadius: 3,
  },
  softSkillScore: {
    fontSize: 8,
    fontWeight: 700,
    color: C.mutedText,
    width: 24,
    textAlign: "right",
  },

  // ── Feedback cards ──
  feedbackCard: {
    border: `1 solid ${C.border}`,
    borderRadius: 8,
    marginBottom: 8,
    backgroundColor: "#fff",
  },
  feedbackHeader: {
    backgroundColor: C.bg,
    paddingHorizontal: 12,
    paddingVertical: 7,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottom: `1 solid ${C.border}`,
  },
  feedbackRound: {
    fontSize: 9,
    fontWeight: 700,
    color: C.darkText,
  },
  ratingBadge: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: C.amberBg,
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    border: `1 solid #FDE68A`,
  },
  ratingText: {
    fontSize: 8,
    fontWeight: 700,
    color: C.amber,
  },
  feedbackBody: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  feedbackMeta: {
    fontSize: 8,
    color: C.mutedText,
    fontWeight: 600,
    marginBottom: 5,
  },
  feedbackText: {
    fontSize: 8,
    color: C.darkText,
    lineHeight: 1.7,
  },
});

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionHeader = ({ title }) => (
  <View style={styles.sectionHeader}>
    <View style={styles.sectionBar} />
    <Text style={styles.sectionTitle}>{title}</Text>
  </View>
);

const ProgressBar = ({ value, max = 100, color = C.blue }) => (
  <View style={styles.progressTrack}>
    <View style={[styles.progressFill, {
      width: `${Math.min(Math.max((value / max) * 100, 0), 100)}%`,
      backgroundColor: color,
    }]} />
  </View>
);

const ScoreBlock = ({ label, value }) => (
  <View style={styles.scoreItem}>
    <Text style={styles.scoreLabel}>{label}</Text>
    <Text style={styles.scoreValue}>{value ?? 0}</Text>
    <ProgressBar value={value ?? 0} />
  </View>
);

const RatingBadge = ({ star }) => (
  <View style={styles.ratingBadge}>
    <Text style={styles.ratingText}>{star} / 5</Text>
  </View>
);

// ─── Main Document ────────────────────────────────────────────────────────────

const CandidatePDFDocument = ({ payload }) => {
  const {
    applicant,
    jobDetails,
    skillArray,
    minSalary,
    maxSalary,
    phoneCode,
    joiningTime,
    softSkills,
    isFromACFL,
    currentSalaryDisplay,
    expectedSalaryDisplay,
    expectedSalaryRaw,
  } = payload;

  const hasCheatingFlag = (applicant?.cheatpercent ?? 0) > 50;
  const hasAIScores =
    (applicant?.aggregate ?? 0) > 0 ||
    (applicant?.resumescore ?? 0) > 0 ||
    (applicant?.voiceanalysisscore ?? 0) > 0 ||
    (applicant?.technicalscore ?? 0) > 0 ||
    (applicant?.aiscore ?? 0) > 0;

  const hasTechSection = (applicant?.technicalscore ?? 0) > 0;
  const hasEmpSection = (applicant?.aiscore ?? 0) > 0;
  const hasSoftSkills = softSkills.length > 0;

  const salaryRange = getSalaryRangeLabel(expectedSalaryRaw, minSalary, maxSalary);

  const ACFL_SCORE_KEYS = [
    "communicationSkills", "personality", "teamwork",
    "attitude", "culturalFitment", "businessUnderstanding",
  ];

  const feedbackRounds = (applicant?.roundsinfo || []).filter((r, idx) => {
    if (idx === 0) return false;
    if (isFromACFL) {
      const hasScore = ACFL_SCORE_KEYS.some((k) => (r[k] || 0) > 0);
      const hasSummary =
        r.interviewSummary &&
        r.interviewSummary.trim() !== "" &&
        r.interviewSummary !== "No summary provided.";
      return hasScore || hasSummary || (r.star || 0) > 0;
    }
    if ((r.star || 0) > 0) return true;
    const fb = r.feedback?.trim() ?? "";
    if (!fb) return false;
    const lower = fb.toLowerCase();
    return lower !== "no feedback provided." && lower !== "no feedback provided";
  });

  const hasFeedbacks = feedbackRounds.length > 0;

  return (
    <Document title={`${applicant?.name || "Candidate"} — Overview`} author="Northstarz.Ai">
      <Page size="A4" style={styles.page}>

        {/* ── Candidate Info ── */}
        <View style={styles.candidateHeader}>
          <View style={styles.nameRow}>
            <Text style={styles.candidateName}>{applicant?.name}</Text>
            {jobDetails?.profilename && (
              <Text style={styles.jobTitle}>| {jobDetails.profilename}</Text>
            )}
            {applicant?.status && (
              <Text style={styles.statusBadge}>{applicant.status}</Text>
            )}
          </View>

          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Email: </Text>
            <Text style={[styles.infoValue, { color: C.blue }]}>{applicant?.email}</Text>
            <Text style={styles.infoLabel}>Phone: </Text>
            <Text style={styles.infoValue}>{phoneCode} {applicant?.phone}</Text>
          </View>

          {applicant?.totalexperience && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Total Experience: </Text>
              <Text style={styles.infoValue}>{applicant.totalexperience}</Text>
            </View>
          )}

          <View style={styles.infoRow}>
            {applicant?.qualification && (
              <>
                <Text style={styles.infoLabel}>Education: </Text>
                <Text style={styles.infoValue}>{applicant.qualification}</Text>
              </>
            )}
            {applicant?.heardaboutus && (
              <>
                <Text style={styles.infoLabel}>Source: </Text>
                <Text style={styles.infoValue}>
                  {/^northstarz(\.ai)?$/i.test(applicant.heardaboutus)
                    ? "Northstarz"
                    : applicant.heardaboutus}
                </Text>
              </>
            )}
          </View>
        </View>

        {/* ── Skills ── */}
        {skillArray?.length > 0 && (
          <View style={styles.section}>
            <SectionHeader title="Skills" />
            <View style={[styles.sectionBody, styles.skillsWrap]}>
              {skillArray.map((s, i) => (
                <Text key={i} style={styles.skillTag}>{s.trim()}</Text>
              ))}
            </View>
          </View>
        )}

        {/* ── Annual Salary + Time to Join ── */}
        <View style={styles.section}>
          <View style={styles.twoCol}>
            {/* Left: Salary */}
            <View style={[styles.col, styles.sectionBody]}>
              <View style={[styles.sectionHeader, {
                borderBottom: 0, paddingHorizontal: 0, paddingTop: 0, paddingBottom: 6,
                borderTopLeftRadius: 0, borderTopRightRadius: 0,
              }]}>
                <View style={styles.sectionBar} />
                <Text style={styles.sectionTitle}>Annual Salary</Text>
                {salaryRange && (
                  <Text style={[styles.salaryBadge, {
                    color: salaryRange.color,
                    borderColor: salaryRange.color,
                  }]}>
                    {salaryRange.label}
                  </Text>
                )}
              </View>
              <View style={styles.salaryRow}>
                <View>
                  <Text style={styles.salaryLabel}>Current Salary</Text>
                  <Text style={styles.salaryValue}>{currentSalaryDisplay}</Text>
                </View>
                <View>
                  <Text style={styles.salaryLabel}>Expected Salary</Text>
                  <Text style={styles.salaryValue}>{expectedSalaryDisplay}</Text>
                </View>
              </View>
            </View>

            <View style={styles.colDivider} />

            {/* Right: Time to Join */}
            <View style={[styles.col, styles.sectionBody]}>
              <View style={[styles.sectionHeader, {
                borderBottom: 0, paddingHorizontal: 0, paddingTop: 0, paddingBottom: 6,
                borderTopLeftRadius: 0, borderTopRightRadius: 0,
              }]}>
                <View style={styles.sectionBar} />
                <Text style={styles.sectionTitle}>Time to Join</Text>
              </View>
              <Text style={{ fontSize: 8, color: C.mutedText, marginTop: 3 }}>
                Approximate time to join:{" "}
                <Text style={{ fontWeight: 700, color: C.darkText }}>{joiningTime} days</Text>
              </Text>
            </View>
          </View>
        </View>

        {/* ── AI Interview Scores (includes Technical & Employability sub-sections) ── */}
        {hasAIScores && (
          <View style={styles.section}>
            <SectionHeader title="AI Interview Scores" />
            <View style={styles.sectionBody}>

              {hasCheatingFlag && (
                <View style={styles.cheatAlert}>
                  <Text style={styles.cheatText}>
                    [!] Unusual behaviour detected — check recorded interview videos for potential cheating.
                  </Text>
                </View>
              )}

              {/* Top score row */}
              <View style={[styles.scoreGrid, { marginBottom: 0 }]}>
                <ScoreBlock label="Aggregate AI Score" value={applicant?.aggregate} />
                <ScoreBlock label="Resume Score" value={applicant?.resumescore} />
                <ScoreBlock label="Voice Score" value={applicant?.voiceanalysisscore} />
              </View>

              {/* Technical Assessment sub-section */}
              {hasTechSection && (
                <View style={styles.subSectionDivider}>
                  <View style={styles.subSectionHeader}>
                    <View style={styles.subSectionBar} />
                    <Text style={styles.subSectionName}>Technical Assessment</Text>
                  </View>
                  <Text style={styles.subSectionTitle}>
                    Technical Score:{" "}
                    <Text style={{ fontWeight: 700, color: C.blue }}>{applicant.technicalscore}</Text>
                  </Text>
                  <ProgressBar value={applicant.technicalscore} />
                </View>
              )}

              {/* Employability Assessment sub-section */}
              {hasEmpSection && (
                <View style={styles.subSectionDivider}>
                  <View style={styles.subSectionHeader}>
                    <View style={styles.subSectionBar} />
                    <Text style={styles.subSectionName}>Employability Assessment</Text>
                  </View>
                  <Text style={styles.subSectionTitle}>
                    Employability Score:{" "}
                    <Text style={{ fontWeight: 700, color: C.blue }}>{applicant.aiscore}</Text>
                  </Text>
                  <ProgressBar value={applicant.aiscore} />

                  {hasSoftSkills && (
                    <View style={{ marginTop: 8 }}>
                      <Text style={[styles.subSectionTitle, { marginBottom: 6 }]}>Soft Skills</Text>
                      {softSkills.map((s, i) => (
                        <View key={i} style={styles.softSkillRow}>
                          <Text style={styles.softSkillLabel}>{s.label}</Text>
                          <View style={styles.softSkillTrack}>
                            <View style={[styles.softSkillFill, { width: `${(s.score / s.max) * 100}%` }]} />
                          </View>
                          <Text style={styles.softSkillScore}>{s.score}/{s.max}</Text>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              )}

            </View>
          </View>
        )}

      </Page>

      {/* ── Page 2: Summaries and Feedbacks ── */}
      {(applicant?.technical_summary || applicant?.employability_summary || hasFeedbacks) && (
        <Page size="A4" style={styles.page}>

          {(applicant?.technical_summary || applicant?.employability_summary) && (
            <View style={styles.section}>
              <SectionHeader title="AI Interview Summary" />
              <View style={styles.sectionBody}>

                {applicant?.technical_summary && (
                  <>
                    <View style={styles.subSectionHeader}>
                      <View style={styles.subSectionBar} />
                      <Text style={styles.subSectionName}>Technical Assessment</Text>
                    </View>
                    <Text style={styles.summaryText}>{stripHtml(applicant.technical_summary)}</Text>
                  </>
                )}

                {applicant?.employability_summary && (
                  <View style={applicant?.technical_summary ? styles.subSectionDivider : null}>
                    <View style={styles.subSectionHeader}>
                      <View style={styles.subSectionBar} />
                      <Text style={styles.subSectionName}>Employability Assessment</Text>
                    </View>
                    <Text style={styles.summaryText}>{stripHtml(applicant.employability_summary)}</Text>
                  </View>
                )}

              </View>
            </View>
          )}

          {/* ── Interview Feedbacks ── */}
          {hasFeedbacks && (
            <View style={styles.section}>
              <SectionHeader title="Interview Feedbacks" />
              <View style={styles.sectionBody}>
                {feedbackRounds.map((r, i) => {
                  const date = getRoundDate(r);
                  const interviewer = r.functionalmanagername || r.assignedInterviewerName;
                  const metaParts = [interviewer, date].filter(Boolean);

                  return (
                    <View key={i} style={styles.feedbackCard} wrap={false}>
                      <View style={styles.feedbackHeader}>
                        <Text style={styles.feedbackRound}>Round {r.roundfrom}</Text>
                        {(r.star || 0) > 0 && <RatingBadge star={r.star} />}
                      </View>
                      <View style={styles.feedbackBody}>
                        {metaParts.length > 0 && (
                          <Text style={styles.feedbackMeta}>{metaParts.join("  ·  ")}</Text>
                        )}
                        {isFromACFL ? (
                          <>
                            {ACFL_SCORE_KEYS.filter((k) => (r[k] || 0) > 0).map((k) => (
                              <Text key={k} style={[styles.feedbackText, { marginBottom: 3 }]}>
                                {k.replace(/([A-Z])/g, " $1").trim()}: {r[k]}/5
                              </Text>
                            ))}
                            {r.interviewSummary && (
                              <Text style={[styles.feedbackText, { marginTop: 5 }]}>
                                Summary: {r.interviewSummary}
                              </Text>
                            )}
                          </>
                        ) : (() => {
                          const fb = r.feedback?.trim() ?? "";
                          const lower = fb.toLowerCase();
                          const hasRealFeedback =
                            fb &&
                            lower !== "no feedback provided." &&
                            lower !== "no feedback provided";
                          return hasRealFeedback
                            ? <Text style={styles.feedbackText}>{fb}</Text>
                            : null;
                        })()}
                      </View>
                    </View>
                  );
                })}
              </View>
            </View>
          )}

        </Page>
      )}
    </Document>
  );
};

export default CandidatePDFDocument;
